import { useState, type FormEvent } from 'react'
import { errorMessage } from '../api/client'
import { Note } from '../components/ui'
import { ShopLockup } from '../components/FlowerMark'
import { shop } from '../lib/shop'
import { useAuth } from './AuthContext'

/** Who logged in last on this device. Not the PIN - only ever the name. */
const LAST_USER = 'wf.last-user'

/**
 * The name to put in the box.
 *
 * <p>Whoever used this device last, and the shop's owner on a device nobody has
 * used yet. It was the owner's name every time, written into the code as
 * "uzair" - wrong for a second shop, and a small daily nuisance at the counter,
 * where the staff account had to clear the owner's name before typing its own.
 *
 * <p>Wrapped because reading storage throws outright in a private window, and a
 * login screen that cannot draw is worse than one with an empty box.
 */
function lastUser(): string {
  try {
    return localStorage.getItem(LAST_USER) || shop.login
  } catch {
    return shop.login
  }
}

export function Login() {
  const { signIn } = useAuth()
  const [username, setUsername] = useState(lastUser)
  const [pin, setPin] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await signIn(username, pin)
      // Only once it worked. Remembering a name that was refused would put a
      // typo in the box every morning until somebody noticed.
      try {
        localStorage.setItem(LAST_USER, username)
      } catch {
        /* Private window, or storage turned off. Nothing is lost but the
           convenience. */
      }
    } catch (err) {
      setError(errorMessage(err))
      setPin('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="login">
      <form className="login__card" onSubmit={submit}>
        <div className="login__plate">
          <h1 className="login__title">
            <ShopLockup width={210} />
          </h1>
        </div>
        <p className="login__sub">حساب کھولنے کے لیے پن لکھیں</p>

        {error && <Note kind="error">{error}</Note>}

        <div className="field">
          <label>نام</label>
          <input
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
          />
        </div>

        <div className="field">
          <label>پن</label>
          <input
            type="password"
            inputMode="numeric"
            value={pin}
            maxLength={10}
            onChange={(event) => setPin(event.target.value.replace(/\D/g, ''))}
            autoComplete="current-password"
          />
        </div>

        <button className="btn" style={{ width: '100%' }} disabled={busy || pin.length < 4}>
          {busy ? 'کھل رہا ہے…' : 'کھولیں'}
        </button>
      </form>
    </div>
  )
}
